/**
 * useSpacesUpload — upload files directly to DigitalOcean Spaces via presigned PUT URLs.
 *
 * Usage:
 *   const { uploadFiles, uploading } = useSpacesUpload(appId, context);
 *   // context: 'chat' | 'task_comment' | 'feed' | 'task_description'
 *   const attachments = await uploadFiles(fileList);
 *   // attachments: [{ file_name, file_key, file_url, file_size, content_type }]
 */

import { useState, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const MAX_FILE_SIZE       = 20 * 1024 * 1024;        // 20 MB — default cap
const MAX_MP4_FILE_SIZE   = 100 * 1024 * 1024;        // 100 MB — MP4 gets a higher cap
const MAX_WEBM_FILE_SIZE  = 200 * 1024 * 1024;        // 200 MB — WEBM video runs larger still
const MAX_APK_FILE_SIZE   = 500 * 1024 * 1024;        // 500 MB — mobile app packages (APK/APKM/AAB/IPA) run larger still
const MAX_EXE_FILE_SIZE   = 2 * 1024 * 1024 * 1024;   // 2 GB — EXE installers run larger still

function maxSizeFor(fileName) {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  if (ext === 'exe') return MAX_EXE_FILE_SIZE;
  if (ext === 'apk' || ext === 'apkm' || ext === 'aab' || ext === 'ipa') return MAX_APK_FILE_SIZE;
  if (ext === 'webm') return MAX_WEBM_FILE_SIZE;
  if (ext === 'mp4') return MAX_MP4_FILE_SIZE;
  return MAX_FILE_SIZE;
}

function maxSizeLabel(maxSize) {
  if (maxSize === MAX_EXE_FILE_SIZE) return '2 GB';
  if (maxSize === MAX_APK_FILE_SIZE) return '500 MB';
  if (maxSize === MAX_WEBM_FILE_SIZE) return '200 MB';
  if (maxSize === MAX_MP4_FILE_SIZE) return '100 MB';
  return '20 MB';
}

// A handful of common types the browser reliably reports a File.type for —
// checked first as a fast path. The real gate is ALLOWED_EXTENSIONS below,
// since most of the formats we support (data/config/3D/etc.) either have no
// standardized MIME type or Chrome/Firefox report an empty File.type for
// them, so extension is the only reliable signal across this whole list.
export const ALLOWED_MIME_TYPES = [
  'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain', 'text/csv', 'text/tab-separated-values',
  'text/markdown', 'text/x-markdown',
  'application/json',
  'application/xml', 'text/xml',
  'application/x-yaml', 'text/yaml',
  'text/html',
  'application/sql', 'application/x-sql',
  'text/javascript', 'application/javascript',
  'text/css',
  'application/zip', 'application/x-zip-compressed',
  'application/octet-stream',
  'video/mp4', 'video/webm', 'video/quicktime',
  'audio/mpeg', 'audio/mp4', 'audio/wav', 'audio/x-wav', 'audio/ogg',
];

// Every extension allowed as a feed/chat/task attachment, grouped to match
// how the product spec groups them. Kept in sync with the mirrored list in
// backend/src/routes/attachments.js — that's the endpoint that actually
// enforces this; the frontend copy only gives instant feedback + the file
// picker's `accept` hint.
export const ALLOWED_EXTENSIONS = new Set([
  // Images
  'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'tiff', 'avif', 'heic',
  'dcm', 'eps', 'nii', 'psd', 'ico',
  // Documents
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'pptx', 'txt', 'csv', 'tsv', 'md',
  'html', 'rtf', 'odt', 'epub', 'mobi', 'eml', 'ics', 'vcf', 'mbox',
  // Archives
  'zip', 'bin', '7z', 'zst', 'bz2', 'gz', 'rar', 'tar', 'xz', 'tbz2', 'tgz',
  // Apps
  'apk', 'apkm', 'aab', 'ipa', 'exe',
  // Audio
  'mp3', 'wav', 'ogg', 'm4a', 'aac', 'aiff', 'flac', 'mid', 'opus', 'pcm', 'wma',
  // Video
  'mp4', 'webm', 'mov', 'avi', 'mpeg', '3gp', 'flv', 'm4v', 'mkv', 'ogv', 'ts', 'wmv',
  // Code
  'js', 'css', 'go', 'java', 'php', 'py', 'rb', 'rs', 'bat', 'c', 'cpp',
  'curl', 'dart', 'http', 'kt', 'lua', 'ps1', 'r', 'scala', 'sh', 'swift',
  'test', 'class', 'h', 'pl', 'vb',
  // Data / config
  'sql', 'yaml', 'yml', 'json', 'xml', 'env', 'graphql', 'ini', 'jsonl',
  'ndjson', 'proto', 'toml', 'cfg', 'conf', 'hcl', 'properties', 'cookie',
  'jwt', 'session', 'arrow', 'avro', 'bson', 'edifact', 'geojson', 'har',
  'hdf5', 'h5', 'msgpack', 'netcdf', 'nc', 'ofx', 'orc', 'parquet', 'qbo', 'qif',
  'sqlite', 'x12', 'htaccess', 'kml',
  // Certificates
  'pem', 'crt', 'key', 'csr',
  // Fonts
  'eot', 'otf', 'ttf', 'woff', 'woff2',
  // Logs
  'log', 'accesslog', 'syslog', 'trace',
  // Partial/in-progress downloads
  'crdownload',
  // Subtitles
  'ass', 'sbv', 'srt', 'vtt',
  // 3D
  'obj', 'stl', 'gltf', '3mf', 'dxf', 'glb', 'ply', 'step', 'iges',
]);

export const ACCEPT_STRING = ['image/*', ...[...ALLOWED_EXTENSIONS].map(ext => `.${ext}`)].join(',');

/**
 * Validate files client-side before upload.
 * Returns { valid: File[], errors: string[] }
 */
export function validateFiles(files) {
  const valid = [];
  const errors = [];
  for (const f of Array.from(files)) {
    const maxSize = maxSizeFor(f.name);
    if (f.size > maxSize) {
      errors.push(`"${f.name}" exceeds the ${maxSizeLabel(maxSize)} limit`);
      continue;
    }
    const ext = f.name.split('.').pop()?.toLowerCase() || '';
    const mimeOk = ALLOWED_MIME_TYPES.includes(f.type);
    const extOk  = ALLOWED_EXTENSIONS.has(ext);
    if (!mimeOk && !extOk) {
      errors.push(`"${f.name}" is not a supported file type`);
      continue;
    }
    valid.push(f);
  }
  return { valid, errors };
}

// Browsers report an empty File.type for most of the less-common extensions
// above, so we supply a sensible Content-Type ourselves for anything we
// recognize; anything else falls back to a generic binary stream, which is
// still a perfectly valid Content-Type for storage/download purposes.
const EXT_MIME_FALLBACK = {
  log:  'text/plain', accesslog: 'text/plain', syslog: 'text/plain', trace: 'text/plain',
  md:   'text/markdown',
  tsv:  'text/tab-separated-values',
  csv:  'text/csv',
  json: 'application/json', jsonl: 'application/json', ndjson: 'application/json',
  xml:  'application/xml',
  yaml: 'application/x-yaml', yml: 'application/x-yaml',
  html: 'text/html',
  sql:  'application/sql',
  js:   'text/javascript',
  css:  'text/css',
  svg:  'image/svg+xml',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  odt:  'application/vnd.oasis.opendocument.text',
  rtf:  'application/rtf',
  epub: 'application/epub+zip',
  wav:  'audio/wav',
  mp3:  'audio/mpeg',
  flac: 'audio/flac',
  aac:  'audio/aac',
  ogg:  'audio/ogg', opus: 'audio/opus', ogv: 'video/ogg',
  mp4:  'video/mp4',
  webm: 'video/webm',
  mov:  'video/quicktime',
  avi:  'video/x-msvideo',
  mkv:  'video/x-matroska',
  zip:  'application/zip',
  '7z': 'application/x-7z-compressed',
  rar:  'application/vnd.rar',
  apk:  'application/vnd.android.package-archive',
  apkm: 'application/octet-stream',
  aab:  'application/octet-stream',
  ipa:  'application/octet-stream',
  exe:  'application/x-msdownload',
  kml:  'application/vnd.google-earth.kml+xml',
  gz:   'application/gzip', tgz: 'application/gzip',
  tar:  'application/x-tar',
  bin:  'application/octet-stream',
  bmp:  'image/bmp',
  tiff: 'image/tiff',
  avif: 'image/avif',
  heic: 'image/heic',
  ico:  'image/vnd.microsoft.icon',
  psd:  'image/vnd.adobe.photoshop',
  ttf:  'font/ttf',
  otf:  'font/otf',
  woff: 'font/woff',
  woff2: 'font/woff2',
  eot:  'application/vnd.ms-fontobject',
  srt:  'application/x-subrip',
  vtt:  'text/vtt',
  pem:  'application/x-pem-file',
  sqlite: 'application/vnd.sqlite3',
  glb:  'model/gltf-binary',
  gltf: 'model/gltf+json',
  obj:  'model/obj',
  stl:  'model/stl',
};

function resolveContentType(file) {
  if (file.type) return file.type;
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  return EXT_MIME_FALLBACK[ext] || 'application/octet-stream';
}

export function useSpacesUpload(appId, context) {
  const { getToken } = useAuth();
  const [uploading, setUploading] = useState(false);

  const UPLOAD_CONCURRENCY = 4;

  const uploadFiles = useCallback(async (files) => {
    if (!files || files.length === 0) return [];
    setUploading(true);
    try {
      // One token for the whole batch — previously this was re-fetched with
      // skipCache per file, forcing a fresh Clerk network round trip for
      // every single attachment even when uploading one small file. The
      // session token's own TTL covers the life of one upload batch, so a
      // single skip-cache fetch up front is enough.
      const token = await getToken({ skipCache: true });
      const fileList = Array.from(files);
      // Indexed so results can be placed back in original file order even
      // though files complete out of order under concurrency.
      const results = new Array(fileList.length);

      const uploadOne = async (file) => {
        const contentType = resolveContentType(file);
        // 1. Get presigned PUT URL from our backend
        const presignRes = await fetch(`${API_URL}/apps/${appId}/attachments/presign`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            file_name:    file.name,
            content_type: contentType,
            file_size:    file.size,
            context,
          }),
        });
        if (!presignRes.ok) {
          const err = await presignRes.json().catch(() => ({}));
          throw new Error(err.error || 'Failed to get upload URL');
        }
        const { upload_url, file_url, file_key } = await presignRes.json();

        // 2. PUT file directly to Spaces (no auth header — presigned URL handles it)
        const putRes = await fetch(upload_url, {
          method: 'PUT',
          headers: { 'Content-Type': contentType, 'x-amz-acl': 'public-read' },
          body: file,
        });
        if (!putRes.ok) throw new Error('Upload to storage failed');

        return {
          file_name:    file.name,
          file_key,
          file_url,
          file_size:    file.size,
          content_type: contentType,
        };
      };

      // Bounded-concurrency worker pool: at most UPLOAD_CONCURRENCY files in
      // flight at once, rather than one-at-a-time (previous behavior) or an
      // unbounded Promise.all (which could fire dozens of simultaneous
      // presign+PUT pairs for a large batch). Same as the previous
      // sequential loop, the first file to fail rejects the whole batch —
      // in-flight uploads for other files are not explicitly cancelled, but
      // their eventual results are simply discarded once uploadFiles throws.
      let nextIndex = 0;
      const worker = async () => {
        while (nextIndex < fileList.length) {
          const i = nextIndex++;
          results[i] = await uploadOne(fileList[i]);
        }
      };
      const workerCount = Math.min(UPLOAD_CONCURRENCY, fileList.length);
      await Promise.all(Array.from({ length: workerCount }, worker));

      return results;
    } finally {
      setUploading(false);
    }
  }, [appId, context, getToken]);

  return { uploadFiles, uploading };
}
